import { Stack } from '@lessonnine/design-system.lib';
// External Imports
import React from 'react';

import { LANDING_PAGE } from '../../config/types/prop.mjs';
import { ListViewContext } from '../../context/listView.mjs';
import { useAccessibilityFocusLastAfterLoading } from '../../hooks/useAccessibilityFocusLastAfterLoading.mjs';
// Internal Imports
import { ContentList } from '../ContentList/ContentList.jsx';
import { Divider } from '../Divider/Divider.jsx';
import { I18n } from '../I18n/I18n.jsx';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly.jsx';
import { ShowMoreButton } from '../ShowMoreButton/ShowMoreButton.jsx';

// Component Definition
function Homepage({ loadingMore, onLoadMore, pagination: { hasMore }, relatedItems }) {
  const handleFocus = useAccessibilityFocusLastAfterLoading({ loading: loadingMore });
  const showLoadMore = hasMore || loadingMore;

  // Sequences of two-up and three-up sections.
  // If more articles can be loaded, only renders complete rows,
  // Otherwise, the last row may be incomplete.
  const feedSequences = hasMore
    ? [
        [relatedItems.slice(0, 2), relatedItems.slice(2, 8)],
        [relatedItems.slice(8, 10), relatedItems.slice(10, -1)],
      ]
    : [
        [relatedItems.slice(0, 2), relatedItems.slice(2, 8)],
        [relatedItems.slice(8, 10), relatedItems.slice(10)],
      ];

  return (
    <section>
      <ScreenReaderOnly as="h1">
        <I18n defaultText="Homepage" key="homepage-h1" />
      </ScreenReaderOnly>
      <Stack>
        {feedSequences.map((sequence) => (
          <>
            <ListViewContext.Provider value="twoUp">
              <ContentList
                interstitials={[
                  {
                    content: <Divider />,
                    count: Number.POSITIVE_INFINITY,
                    frequency: 3,
                  },
                ]}
                items={sequence[0]}
                onFocus={handleFocus}
              />
            </ListViewContext.Provider>
            <Divider />
            <ListViewContext.Provider value="threeUp">
              <ContentList
                interstitials={[
                  {
                    content: <Divider />,
                    count: Number.POSITIVE_INFINITY,
                    frequency: 3,
                  },
                ]}
                items={sequence[1]}
                onFocus={handleFocus}
              />
            </ListViewContext.Provider>
            <Divider />
          </>
        ))}
        {showLoadMore && (
          <ShowMoreButton disabled={loadingMore} loading={loadingMore} onClick={onLoadMore} />
        )}
      </Stack>
    </section>
  );
}

Homepage.propTypes = LANDING_PAGE;

// Module Exports
export { Homepage };
